import React from 'react';
import PageShell from '../../components/PageShell';
import { SectionHeading, IconBadge, GradientButton, CTABanner, StepChip } from '../../components/common/Primitives';
import LicenceFeeHighlight from '../../components/common/LicenceFeeHighlight';
import { COACHING_ADOPTION } from '../../lib/mockCoaching';
import { Check, Plus } from 'lucide-react';

const toneMap = {
  purple: 'text-purple', royal: 'text-royal', cyan: 'text-cyan', mint: 'text-mint-600',
};

export default function CoachingAdoption() {
  const d = COACHING_ADOPTION;
  return (
    <PageShell>
      {/* HERO */}
      <section className="px-6 pt-12 pb-8" data-testid="coaching-adoption-hero">
        <div className="mx-auto max-w-[1240px]">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1.15fr_1fr]">
            <div>
              <div className="inline-flex items-center rounded-full bg-purple-50 px-3 py-1 text-[11px] font-bold tracking-widest text-purple">{d.hero.eyebrow}</div>
              <h1 className="mt-3 text-[40px] md:text-[50px] font-extrabold leading-[1.05] tracking-tight text-navy">{d.hero.title}</h1>
              <p className="mt-5 max-w-2xl text-[15.5px] leading-relaxed text-ink-500">{d.hero.desc}</p>
              <div className="mt-6"><LicenceFeeHighlight /></div>
              <div className="mt-6"><GradientButton to={d.hero.ctaPrimary.to} data-testid="coaching-adoption-cta">{d.hero.ctaPrimary.label}</GradientButton></div>
            </div>
            <div className="relative rounded-3xl bg-white p-6 text-white grain overflow-hidden flex flex-col justify-center min-h-[280px] border border-canvas-200 shadow-soft">
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/c/cb/Meta_Quest_3S_Display_Unit.jpg"
                alt="Meta Quest 3S mixed-reality headset"
                loading="lazy"
                className="pointer-events-none absolute inset-0 h-full w-full object-contain object-center select-none"
                data-testid="coaching-deployment-hero-headset"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-navy/85 via-navy/45 to-transparent" />
              <div className="relative">
                <div className="text-[10.5px] font-bold tracking-widest text-white/85">{d.hero.metricEyebrow}</div>
                <div className="mt-2 text-[64px] font-extrabold leading-none">{d.hero.metricValue}</div>
                <div className="mt-2 text-[13px] text-white/90 max-w-[16rem]">{d.hero.metricNote}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MINIMUM STANDARD */}
      <section className="px-6 py-14">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.minimum.eyebrow} title={d.minimum.title} desc={d.minimum.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {d.minimum.items.map((r, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft">
                <div className={`text-[26px] font-extrabold ${toneMap[r.color]}`}>{r.k}</div>
                <h3 className="mt-2 text-[14.5px] font-bold text-navy">{r.title}</h3>
                <p className="mt-1 text-[12.5px] leading-relaxed text-ink-500">{r.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEPARATION */}
      <section className="px-6 py-14 bg-white">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.separation.eyebrow} title={d.separation.title} desc={d.separation.desc} />
          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="rounded-2xl bg-gradient-to-br from-mint-50 via-cyan-50 to-purple-50 p-6 shadow-soft ring-1 ring-mint/20">
              <div className="flex items-center gap-2">
                <span className="grid h-9 w-9 place-items-center rounded-full bg-mint text-white shadow"><Check className="h-4 w-4" /></span>
                <h3 className="text-[16px] font-bold text-navy">{d.separation.included.title}</h3>
              </div>
              <ul className="mt-4 space-y-2">
                {d.separation.included.items.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-[13px] text-ink-700"><Check className="mt-0.5 h-4 w-4 text-mint flex-none" /> {r}</li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-canvas-200 bg-white p-6 shadow-soft">
              <div className="flex items-center gap-2">
                <span className="grid h-9 w-9 place-items-center rounded-full bg-royal text-white shadow"><Plus className="h-4 w-4" /></span>
                <h3 className="text-[16px] font-bold text-navy">{d.separation.funded.title}</h3>
              </div>
              <ul className="mt-4 space-y-2">
                {d.separation.funded.items.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-[13px] text-ink-700"><span className="mt-1.5 inline-block h-1.5 w-1.5 flex-none rounded-full bg-royal" /> {r}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* OPERATING MODEL */}
      <section className="px-6 py-14">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.operating.eyebrow} title={d.operating.title} desc={d.operating.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {d.operating.steps.map((s, i) => (
              <div key={i} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft">
                <IconBadge icon={s.icon} color={s.color} size="lg" />
                <h3 className="mt-4 text-[15px] font-bold text-navy">{s.title}</h3>
                <p className="mt-1 text-[13px] leading-relaxed text-ink-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PATHWAY */}
      <section className="px-6 py-14 bg-white">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow={d.pathway.eyebrow} title={d.pathway.title} desc={d.pathway.desc} />
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {d.pathway.stages.map((s) => (
              <div key={s.n} className="rounded-2xl border border-canvas-200 bg-white p-5 shadow-soft">
                <StepChip n={s.n} color={s.color} />
                <h3 className="mt-4 text-[15px] font-bold text-navy">{s.title}</h3>
                <p className="mt-1 text-[13px] leading-relaxed text-ink-500">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 pb-24">
        <div className="mx-auto max-w-[1240px]">
          <CTABanner title={d.cta.title} primary={d.cta.primary} />
        </div>
      </section>
    </PageShell>
  );
}
